/**
 * A fund's fees (IP-98..IP-102; the owner's decisions of 1 Oct 2026; IP-136,
 * IP-137 of 6 Oct 2026): what a fee configuration means, and what a change to
 * one must satisfy. Pure and browser-safe (decimal.js and dealing-date.js
 * only, no Node module), so the Console's Settings > Fund config computes and
 * words them exactly as the server does.
 *
 * Fees are percentages only (IP-136, the owner's confirmation of 6 Oct 2026).
 * IP-99's PER_UNIT exit fee and IP-100's FIXED entry fee are retired: the
 * catalogue takes neither (authority/catalogue.js feeProposal), migration 0020
 * refuses either on an open fund_config row or an open proposal, and nothing
 * here computes one. A closed row or a decided proposal from before 0020 may
 * still name one; it is read back as its basis with no value, and worded as
 * retired (describeExitFee, describeEntryFee).
 *
 *   The exit fee (IP-98, IP-99) is charged on the units a redemption or a
 *   switch-out takes from YOUNG lots - dealt before the lot's acquired date
 *   plus exit_fee_holding_months CALENDAR months - and reaches the investor
 *   through the exit-fee price the NAV run strikes (bwef):
 *     PERCENT   bwef = round4(buying x (1 - rate)), 0 < rate <= 0.10
 *     NONE      no bwef: the fund charges no exit fee
 *   Allocation's lot-by-lot arithmetic (IP-54) then charges
 *   fee = Sigma round2(young units x (buying - bwef)).
 *
 *   The entry fee is a percentage of a subscription's amount, 0 <= rate < 1
 *   (0: no fee). Allocation charges round2(amount x rate) of the rule in force
 *   on the dealing date (IP-137, approval/fees.js suggestedFee), and
 *   repricing shows the same figure while the order is open. Nobody sets a
 *   fee order by order: the maker's fee at review (IP-85) is gone too.
 *
 * A change (IP-101) names an effective date E; a person proposes it and a
 * DIFFERENT person approves it, each with a fresh code, and both legs check,
 * at their own date:
 *
 *   the date   E is on or after the first business day AFTER today in
 *              Colombo (FEE_EFFECTIVE_DATE_TOO_EARLY) - never today: today's
 *              orders were taken on today's terms, and a NAV run, the review
 *              and allocation all read D's configuration, so a change on D
 *              could price D under one fee and deal it under another; E is a
 *              business day the calendar knows (..._NOT_BUSINESS_DAY); E is
 *              after every row already scheduled (..._NOT_AFTER_SCHEDULED),
 *              so valid_to is written once; no NAV run or price exists on or
 *              after E (..._PRICED - unreachable while the first rule holds,
 *              kept as the last word); and no order the Core has already
 *              taken - one received after a cut-off or on a holiday, or one
 *              for a requested future date - deals on or after E
 *              (..._ORDERS_PENDING): an order is dealt on the terms in force
 *              when it was taken, and the exit fee is never changed at review;
 *   the fees   the basis agrees with fund.is_exit_fee_required, LIV's flag
 *              and still the on/off switch (FEE_EXIT_FLAG_MISMATCH); and
 *              something changes (FEE_CONFIG_UNCHANGED). The rates' own
 *              ranges are the catalogue's, so a proposal never reaches these
 *              rules with a rate the columns would refuse. IP-99's PER_UNIT
 *              ceiling (FEE_EXIT_PER_UNIT_NO_PRICE, _TOO_HIGH) and IP-100's
 *              FIXED floor (FEE_ENTRY_FIXED_NOT_BELOW_MINIMUM) went with their
 *              bases (IP-136).
 *
 * firstEffectiveDate() is the first date every one of those date rules
 * allows: what Settings > Fund config offers and pre-fills.
 *
 * IP-204: a change may also name the fund's two Apply fees conditions
 * (feeDefaults: an LKR amount a transaction must be MORE THAN for its box to
 * start ticked, or null for always ticked) - read by defaultsOfConfig, worded
 * by describeFeeDefaults, compared by sameDefaults. A change that names none
 * keeps the conditions in force (feeColumnsOf), and its params, and so its
 * digest, are those of a proposal made before IP-204 (paramsOfProposal).
 */
import { cmp, mul, sub, toFixed } from '../decimal.js';
import { DomainError } from '../errors.js';
import { calendarFrom } from '../orders/dealing-date.js';

/**
 * The bases a fee may have (IP-136). FUND_FEES_PROPOSE keeps naming the entry
 * fee's basis, always PERCENT, so the params a proposal made before 0020 was
 * bound to - and the digest its approval re-derives - are unchanged.
 */
export const EXIT_FEE_BASIS = Object.freeze({ NONE: 'NONE', PERCENT: 'PERCENT' });
export const ENTRY_FEE_BASIS = Object.freeze({ PERCENT: 'PERCENT' });

/** The most a PERCENT exit fee may be (0006's CHECK). */
export const MAX_EXIT_FEE_RATE = '0.10';
/** The most a transaction charge rate may be (0034's fund_config_transaction_charge_rate_range, IP-179). */
export const MAX_CHARGE_RATE = '0.05';
/** fund_config.exit_fee_holding_months: 1 to 120, 12 unless set (IP-98). */
export const MIN_HOLDING_MONTHS = 1;
export const MAX_HOLDING_MONTHS = 120;
export const DEFAULT_HOLDING_MONTHS = 12;

const PRICE_DP = 4;
const RATE_DP = 6;

const refuse = (code, message, details = null) => new DomainError(code, message, { status: 409, details });

const isSet = (value) => value !== null && value !== undefined;

/** A fraction as a percentage, its trailing zeros dropped: '0.015000' -> '1.5', '0.010000' -> '1'. */
export function percentText(rate) {
  const text = toFixed(mul(rate, '100'), RATE_DP - 2);
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}

const months = (n) => n + (n === 1 ? ' month' : ' months');

// ---------------------------------------------------------------------------
// The arithmetic
// ---------------------------------------------------------------------------

/**
 * IP-99: the exit-fee price of a buying price under a fee, or null when the
 * fund charges none. A PERCENT rate is at most 0.10, so the price is always
 * above zero for a buying price that is.
 *
 * Any other basis is a TypeError, never a price: a PER_UNIT fee (retired,
 * IP-136) survives only on a closed fund_config row, which pricing refuses to
 * price under before it gets here (pricing/assemble.js navAcceptReadiness).
 *
 * @param {string|null} buyingPrice
 * @param {{ basis: 'NONE'|'PERCENT'|null, value: string|null }|null} fee
 */
export function exitFeePriceOf(buyingPrice, fee) {
  if (!isSet(buyingPrice) || !fee || !isSet(fee.basis) || fee.basis === EXIT_FEE_BASIS.NONE) return null;
  if (fee.basis === EXIT_FEE_BASIS.PERCENT) return toFixed(mul(buyingPrice, sub('1', fee.value)), PRICE_DP);
  throw new TypeError('an exit-fee basis is NONE or PERCENT (IP-136), not ' + String(fee.basis));
}

// ---------------------------------------------------------------------------
// Configurations and proposals, as the Console reads them
// ---------------------------------------------------------------------------

/**
 * The fees of a fund_config row (as q returns it): { exitFee: { basis,
 * value, holdingMonths }, entryFee: { basis, value } }, values at their
 * column's scale. A row with no exit basis is NONE; the entry fee is its rate.
 *
 * The retired value columns (exit_fee_per_unit, entry_fee_fixed) are not
 * read (fees/sql.js CONFIG_COLUMNS selects neither). A closed row from before
 * 0020 whose generated exit_fee_basis still says PER_UNIT comes back as that
 * basis with no value - worded as retired, never priced or charged.
 */
export function feesOfConfig(row) {
  const exitBasis = row.exit_fee_basis ?? EXIT_FEE_BASIS.NONE;
  return {
    exitFee: {
      basis: exitBasis,
      value: exitBasis === EXIT_FEE_BASIS.PERCENT ? toFixed(row.exit_fee_pct, RATE_DP) : null,
      holdingMonths: Number(row.exit_fee_holding_months ?? DEFAULT_HOLDING_MONTHS),
    },
    entryFee: { basis: ENTRY_FEE_BASIS.PERCENT, value: toFixed(row.entry_fee_rate ?? '0', RATE_DP) },
    // IP-204: the Apply fees conditions (null: off - the box always starts ticked).
    defaults: defaultsOfConfig(row),
  };
}

// ---------------------------------------------------------------------------
// IP-204: the Apply fees conditions
// ---------------------------------------------------------------------------

const MONEY_DP = 2;
const moneyOrNull = (value) => (isSet(value) ? toFixed(String(value), MONEY_DP) : null);

/**
 * IP-204: a fund_config row's two Apply fees conditions, each an LKR amount at
 * 2 places or null (off): { entryAbove, exitAbove }. A leg's box starts
 * ticked only when its value is MORE THAN the amount (approval/fees.js
 * feeDefaultOf).
 */
export function defaultsOfConfig(row) {
  return { entryAbove: moneyOrNull(row?.entry_fee_default_above), exitAbove: moneyOrNull(row?.exit_fee_default_above) };
}

/** True when two sets of conditions say the same: both off, or the same amount. */
export function sameDefaults(a, b) {
  const same = (x, y) => (isSet(x) && isSet(y) ? cmp(x, y) === 0 : !isSet(x) && !isSet(y));
  return same(a?.entryAbove, b?.entryAbove) && same(a?.exitAbove, b?.exitAbove);
}

/** LKR at 2 places with thousands separators: '100000' -> 'LKR 100,000.00'. Browser-safe, no Intl. */
export function lkrText(amount) {
  const [whole, cents] = toFixed(String(amount), MONEY_DP).split('.');
  return 'LKR ' + whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + cents;
}

/**
 * The one wording of one condition: what the box does by default. The words
 * say MORE THAN, never "from": "from LKR 100,000.00" reads as 100,000.00 or
 * more, the wrong rule.
 */
export function describeDefaultCondition(amount) {
  if (!isSet(amount)) return 'Always ticked';
  return 'Ticked only when more than ' + lkrText(amount);
}

/**
 * The one wording of both conditions, for the summaries, the reads and the
 * Console. null - a proposal that names none - keeps the ones in force.
 */
export function describeFeeDefaults(defaults) {
  if (!defaults) return 'Unchanged';
  return 'Front-end fee: ' + describeDefaultCondition(defaults.entryAbove) + '. Exit fee: ' + describeDefaultCondition(defaults.exitAbove) + '.';
}

/**
 * IP-179: a fund_config row's transaction charge rate at its scale. Kept apart
 * from the fees (feesOfConfig): it prices the NAV run, and is no term of an
 * order already taken (books/close.js compares the fees alone).
 *
 * The column is NOT NULL, so a row without it was read through a column list
 * that leaves it out - a fault, never a rate of 0: an approval of a proposal
 * made before IP-179 would otherwise write 0 over the rate in force.
 */
export function chargeRateOfConfig(row) {
  if (!isSet(row?.transaction_charge_rate)) {
    throw new TypeError('chargeRateOfConfig needs the fund_config row read with transaction_charge_rate (fees/sql.js CONFIG_COLUMNS)');
  }
  return toFixed(row.transaction_charge_rate, RATE_DP);
}

/**
 * The one wording of a transaction charge rate: a percentage of the NAV
 * file's charge base, or none at 0. null - a proposal made before IP-179 -
 * keeps the rate in force.
 */
export function describeChargeRate(rate) {
  if (!isSet(rate)) return 'Unchanged';
  if (cmp(rate, '0') === 0) return 'None';
  return percentText(rate) + ' % of the charge base';
}

/** The fees FUND_FEES_PROPOSE's params name, in feesOfConfig's shape. */
export function feesOfParams(params) {
  return {
    exitFee: { basis: params.exitFeeBasis, value: params.exitFeeValue ?? null, holdingMonths: params.exitFeeHoldingMonths },
    entryFee: { basis: params.entryFeeBasis, value: params.entryFeeValue },
    // IP-204: null when the proposal names no conditions - it keeps the ones in force.
    defaults: params.feeDefaults ?? null,
  };
}

/** A basis IP-136 retired, as a decided proposal from before 0020 still names it: never a figure to charge. */
const retired = (basis) => 'A ' + basis + ' fee, retired: fees are percentages only';

/** The one wording of an exit fee the summaries, the reads and the Console share. */
export function describeExitFee(fee) {
  if (!fee || !isSet(fee.basis) || fee.basis === EXIT_FEE_BASIS.NONE) return 'None';
  if (fee.basis !== EXIT_FEE_BASIS.PERCENT) return retired(fee.basis);
  return percentText(fee.value) + ' % of the buying price, on units held less than ' + months(fee.holdingMonths);
}

/**
 * The one wording of an entry fee: the rate allocation charges (IP-137), no
 * longer a suggestion the maker may change - 0 is no fee at all.
 */
export function describeEntryFee(fee) {
  if (!fee || !isSet(fee.basis)) return 'None';
  if (fee.basis !== ENTRY_FEE_BASIS.PERCENT) return retired(fee.basis);
  if (cmp(fee.value ?? '0', '0') === 0) return 'None';
  return percentText(fee.value) + ' % of the amount';
}

/** True when two fee sets charge the same: the holding period counts only while there is an exit fee. */
export function sameFees(a, b) {
  const sameValue = (x, y) => (isSet(x) && isSet(y) ? cmp(x, y) === 0 : !isSet(x) && !isSet(y));
  if (a.exitFee.basis !== b.exitFee.basis || !sameValue(a.exitFee.value, b.exitFee.value)) return false;
  if (a.exitFee.basis !== EXIT_FEE_BASIS.NONE && a.exitFee.holdingMonths !== b.exitFee.holdingMonths) return false;
  return a.entryFee.basis === b.entryFee.basis && sameValue(a.entryFee.value, b.entryFee.value);
}

// ---------------------------------------------------------------------------
// The effective date (IP-101)
// ---------------------------------------------------------------------------

/**
 * The first date a change may take effect from: the first business day after
 * today (never today; see the header). Throws CalendarError when the loaded
 * calendar holds no business day after today.
 *
 * @param {object} calendar anything calendarFrom() accepts, from today on
 * @param {string} today YYYY-MM-DD, Colombo
 */
export function earliestEffectiveDate(calendar, today) {
  return calendarFrom(calendar).addBusinessDays(today, 1);
}

/**
 * The first date a change may name: the first business day after today,
 * after the fund's latest row (valid_to is written once) and after the latest
 * dealing date of an order the Core has already taken - the date Settings >
 * Fund config offers and pre-fills, so it is one the server accepts. Throws
 * CalendarError when the loaded calendar holds no business day after the
 * latest of the three.
 *
 * @param {object} calendar anything calendarFrom() accepts, from today on
 * @param {{ today: string, latestValidFrom?: string|null, pendingThrough?: string|null }} facts
 *   pendingThrough: the latest dealing date of the fund's open orders (fees/sql.js selectPendingOrders), or null
 */
export function firstEffectiveDate(calendar, { today, latestValidFrom = null, pendingThrough = null }) {
  const after = [today, latestValidFrom, pendingThrough].filter(isSet).sort().at(-1);
  return calendarFrom(calendar).addBusinessDays(after, 1);
}

/**
 * Why a change may not take effect from E, or null. In this order: too
 * early, not a business day, not after a row already scheduled, priced,
 * orders already taken that deal on or after it.
 *
 * @param {object} args
 * @param {string} args.effectiveDate E
 * @param {string} args.earliest earliestEffectiveDate(...)
 * @param {boolean|null} args.businessDay the calendar's flag for E; null when it has no row for E
 * @param {string|null} args.latestValidFrom the latest fund_config.valid_from of the fund
 * @param {string|null} args.pricedOn the first date on or after E with a NAV run or a price, or null
 * @param {{ latestDealingDate: string, orders: number }|null} [args.pending] the fund's open orders (not
 *   REJECTED, CANCELLED or EXPIRED) that deal on or after E, or null when there are none
 * @returns {DomainError|null}
 */
export function effectiveDateRefusal({ effectiveDate, earliest, businessDay, latestValidFrom = null, pricedOn = null, pending = null }) {
  if (effectiveDate < earliest) {
    return refuse('FEE_EFFECTIVE_DATE_TOO_EARLY', 'Fees can take effect from ' + earliest + ' at the earliest - the next business day - not '
      + effectiveDate + '. A change never takes effect on the day it is made: that day\'s transactions were taken, and are priced, on its fees.',
    { effectiveDate, earliest });
  }
  if (businessDay !== true) {
    return refuse('FEE_EFFECTIVE_DATE_NOT_BUSINESS_DAY', businessDay === null
      ? 'The dealing calendar has no row for ' + effectiveDate + ', so it cannot be an effective date. Choose a date the calendar holds.'
      : effectiveDate + ' is not a business day. Fees take effect from a dealing day.', { effectiveDate });
  }
  if (isSet(latestValidFrom) && effectiveDate <= latestValidFrom) {
    return refuse('FEE_EFFECTIVE_DATE_NOT_AFTER_SCHEDULED', 'The fund already has dealing rules from ' + latestValidFrom
      + '; a change must take effect after that date, not from ' + effectiveDate + '.', { effectiveDate, latestValidFrom });
  }
  if (isSet(pricedOn)) {
    return refuse('FEE_EFFECTIVE_DATE_PRICED', 'A NAV run or price already exists for ' + pricedOn + ', on or after ' + effectiveDate
      + '; fees cannot change under a price already struck.', { effectiveDate, pricedOn });
  }
  if (pending && pending.orders > 0) {
    const one = pending.orders === 1;
    return refuse('FEE_EFFECTIVE_DATE_ORDERS_PENDING', (one ? 'A transaction' : pending.orders + ' transactions') + ' already taken for this fund '
      + (one ? 'deals' : 'deal') + ' on or after ' + effectiveDate + ' (the latest on ' + pending.latestDealingDate + '), on the fees in force when '
      + (one ? 'it was' : 'they were') + ' taken. Fees can take effect from after ' + pending.latestDealingDate + '.',
    { effectiveDate, latestDealingDate: pending.latestDealingDate, orders: pending.orders });
  }
  return null;
}

// ---------------------------------------------------------------------------
// The fees themselves (IP-99, IP-136)
// ---------------------------------------------------------------------------

/**
 * Why these fees may not be configured for the fund, or null. In this order:
 * the flag, no change. The rates are the catalogue's to range (an exit fee
 * above 0 and at most 0.10, an entry fee from 0 up to but not 1), and it takes
 * no other basis (IP-136).
 *
 * @param {object} params FUND_FEES_PROPOSE's params (or paramsOfProposal of a stored row)
 * @param {object} facts
 * @param {boolean} facts.isExitFeeRequired fund.is_exit_fee_required
 * @param {object} facts.current feesOfConfig() of the open fund_config row
 * @param {string|null} [facts.currentChargeRate] chargeRateOfConfig() of it (IP-179): a proposal that changes only
 *   the charge rate is a change
 * @returns {DomainError|null}
 */
export function feeRefusal(params, { isExitFeeRequired, current, currentChargeRate = null }) {
  const charges = params.exitFeeBasis !== EXIT_FEE_BASIS.NONE;
  if (charges !== (isExitFeeRequired === true)) {
    return refuse('FEE_EXIT_FLAG_MISMATCH', isExitFeeRequired
      ? 'The fund charges an exit fee (LIV\'s isExitFeeRequired): its exit fee is a percentage of the buying price, not NONE.'
      : 'The fund charges no exit fee (LIV\'s isExitFeeRequired is false): its exit fee is NONE. Turning the exit fee on is a change to the fund itself.',
    { isExitFeeRequired: isExitFeeRequired === true, exitFeeBasis: params.exitFeeBasis });
  }
  const sameRate = !isSet(params.transactionChargeRate) || !isSet(currentChargeRate) || cmp(params.transactionChargeRate, currentChargeRate) === 0;
  // IP-204: a proposal that names no conditions keeps them; one that does changes something only if they differ.
  const sameConditions = !params.feeDefaults || !current?.defaults || sameDefaults(params.feeDefaults, current.defaults);
  if (current && sameFees(feesOfParams(params), current) && sameRate && sameConditions) {
    return refuse('FEE_CONFIG_UNCHANGED', 'These are the fees, the transaction charge rate and the Apply fees conditions the fund already has; nothing would change.');
  }
  return null;
}

// ---------------------------------------------------------------------------
// The columns (fund_config and fund_fee_proposal), and back
// ---------------------------------------------------------------------------

/**
 * fund_config's fee columns for these fees - every one fees/sql.js
 * FEE_COLUMNS names, so insertConfigFrom writes them all. The retired
 * exit_fee_per_unit and entry_fee_fixed are not among them: the new row takes
 * their NULL default, as 0020 holds every open row to.
 *
 * exit_fee_holding_days is read by nothing since IP-98; on a PERCENT row it is
 * written as round(months x 365 / 12) - 365 for 12 - so code from before
 * 0011, if it were ever rolled back, still charges a PERCENT fee over about
 * the same period instead of none. On a NONE row it is NULL, so the dev
 * seed's fill of exit_fee_pct (0060: holding days set, no rate) never gives a
 * fund that charges no exit fee one.
 *
 * IP-179: the transaction charge rate is the proposal's, or - a proposal made
 * before it names none - the rate of the row the change replaces.
 *
 * IP-204: the Apply fees conditions likewise - the proposal's feeDefaults, or
 * the replaced row's when it names none.
 *
 * @param {object} params FUND_FEES_PROPOSE's params (or paramsOfProposal of a stored row)
 * @param {{ currentChargeRate: string, currentDefaults?: { entryAbove: string|null, exitAbove: string|null }|null }} replaced
 *   chargeRateOfConfig() and defaultsOfConfig() of the row the change replaces
 */
export function feeColumnsOf(params, { currentChargeRate, currentDefaults = null } = {}) {
  const charges = params.exitFeeBasis === EXIT_FEE_BASIS.PERCENT;
  const chargeRate = params.transactionChargeRate ?? currentChargeRate;
  if (!isSet(chargeRate)) throw new TypeError('feeColumnsOf needs the charge rate the proposal names or the one it replaces');
  // IP-204: the conditions proposed, else the replaced row's. A condition carried over on a fee the new row no
  // longer charges is dropped (0039's fund_config_*_default_needs_fee would refuse it): there is no box to tick.
  const defaults = params.feeDefaults ?? currentDefaults ?? { entryAbove: null, exitAbove: null };
  const chargesEntry = isSet(params.entryFeeValue) && cmp(params.entryFeeValue, '0') > 0;
  return {
    exit_fee_pct: charges ? params.exitFeeValue : null,
    exit_fee_holding_months: params.exitFeeHoldingMonths,
    exit_fee_holding_days: charges ? Math.round((params.exitFeeHoldingMonths * 365) / 12) : null,
    entry_fee_rate: params.entryFeeValue,
    transaction_charge_rate: chargeRate,
    entry_fee_default_above: chargesEntry ? defaults.entryAbove ?? null : null,
    exit_fee_default_above: charges ? defaults.exitAbove ?? null : null,
  };
}

/**
 * fund_fee_proposal's fee columns for these fees: the bases explicit (0011's
 * NOT NULL columns, and what fund_fee_proposal_percent_only holds an open
 * proposal to), each rate in its column. The retired columns take their NULL
 * default.
 */
export function proposalColumnsOf(params) {
  return {
    exit_fee_basis: params.exitFeeBasis,
    exit_fee_pct: params.exitFeeBasis === EXIT_FEE_BASIS.PERCENT ? params.exitFeeValue : null,
    exit_fee_holding_months: params.exitFeeHoldingMonths,
    entry_fee_basis: params.entryFeeBasis,
    entry_fee_rate: params.entryFeeValue,
    transaction_charge_rate: params.transactionChargeRate ?? null,
    // IP-204: whether the proposal names the conditions, and what it names (each NULL for off).
    default_conditions_proposed: Boolean(params.feeDefaults),
    entry_fee_default_above: params.feeDefaults?.entryAbove ?? null,
    exit_fee_default_above: params.feeDefaults?.exitAbove ?? null,
  };
}

/**
 * The FUND_FEES_PROPOSE params a stored proposal was made from, exactly as
 * the catalogue normalised them (values at their scale) - so the approval can
 * re-derive the digest the proposer's code was bound to. The keys are the
 * ones every proposal has had since 0011 (IP-136 narrowed the bases, not the
 * shape), so a PERCENT proposal made before 0020 still matches its digest.
 * Only an open proposal is ever approved, and 0020 holds it to a percentage;
 * a decided one from before may name a retired basis, which comes back with no
 * value - its history, worded as retired, never a fee to apply.
 *
 * IP-179: transactionChargeRate is named only when the proposal holds one - a
 * proposal stored before it has none, and its proposer's code was bound to
 * params without the key.
 *
 * IP-204: feeDefaults likewise, only when default_conditions_proposed - the
 * column, not the amounts, says whether it was named, since null amounts are
 * a proposal to switch the conditions off.
 */
export function paramsOfProposal(row) {
  const exitFeeBasis = row.exit_fee_basis;
  const entryFeeBasis = row.entry_fee_basis;
  return {
    fundId: row.fund_id,
    effectiveDate: row.effective_date,
    exitFeeBasis,
    exitFeeValue: exitFeeBasis === EXIT_FEE_BASIS.PERCENT ? toFixed(row.exit_fee_pct, RATE_DP) : null,
    exitFeeHoldingMonths: Number(row.exit_fee_holding_months),
    entryFeeBasis,
    entryFeeValue: entryFeeBasis === ENTRY_FEE_BASIS.PERCENT ? toFixed(row.entry_fee_rate, RATE_DP) : null,
    ...(isSet(row.transaction_charge_rate) ? { transactionChargeRate: toFixed(row.transaction_charge_rate, RATE_DP) } : {}),
    // IP-204: only a proposal that named the conditions has the key - an older one was bound without it.
    ...(row.default_conditions_proposed === true
      ? { feeDefaults: { entryAbove: moneyOrNull(row.entry_fee_default_above), exitAbove: moneyOrNull(row.exit_fee_default_above) } }
      : {}),
    reason: row.reason,
  };
}
